import { useTheme } from "../../../hooks/useTheme";
import { memo, useCallback, useMemo } from "react";
import { isDesktop } from "react-device-detect";
import ProfilePic from "../../ProfilePic";
import {
  AccountData,
  Follower,
  MembershipPlan,
  ThemeType,
} from "@markit/common.types";
import RectangleButton from "../../Buttons/RectangleButton";
import { Colors } from "../../../utils/colors";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { Icon } from "@iconify/react";
import { onProfilePageNavigatePath } from "../../../utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import { checkIsDesktop } from "../../../utils/screenResponsivenessUtils";
import { useWindowResize } from "../../../hooks/useWindowResize";
import { generateTextFromJSON } from "../../../utils/stringUtils";
import MembershipPlanCardDesktop from "./MembershipPlanCardDesktop";
import ReadOnlyRichTextEditor from "../../TextFields/ReadOnlyRichTextEditor";

type SubscribeFlowMembershipProps = {
  creatorData: AccountData;
  chooseOnPress: (selectedMembership: MembershipPlan) => void;
  selectedMembershipPlan: MembershipPlan | undefined;
  setSelectedMembershipPlan: (selectedMembershipPlan: MembershipPlan) => void;
  visibleMembershipPlans: MembershipPlan[];
  followerData?: Follower;
  themeType?: ThemeType;
};

const SubscribeFlowMembership = (props: SubscribeFlowMembershipProps) => {
  const {
    creatorData,
    chooseOnPress,
    selectedMembershipPlan,
    setSelectedMembershipPlan,
    visibleMembershipPlans,
    followerData,
    themeType,
  } = props;
  const navigate = useNavigate();
  const windowWidth = useWindowResize();
  const { theme } = useTheme(themeType);

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth, true),
    [windowWidth]
  );

  const styles = {
    cardContainer: {
      paddingBlock: 14,
      borderRadius: 14,
      width: 280,
      minHeight: 327,
      cursor: "pointer",
    },
  };

  // Helps check if membership body is empty or not
  const membershipDescription = useMemo(
    () =>
      selectedMembershipPlan
        ? generateTextFromJSON(selectedMembershipPlan.body)
        : undefined,
    [selectedMembershipPlan]
  );

  const currentMembershipPlan = useMemo(
    () =>
      creatorData.membership.membershipPlans.find(
        (plan) => plan.id === followerData?.membershipPlanId
      ),
    [creatorData.membership.membershipPlans, followerData?.membershipPlanId]
  );

  const navigateProfilePage = useCallback(
    () => onProfilePageNavigatePath(navigate, creatorData.username),
    [creatorData.username, navigate]
  );

  const isCurrentMembershipPlan = useCallback(
    (membershipId: string) =>
      followerData ? followerData.membershipPlanId === membershipId : false,
    [followerData]
  );

  const renderCardButton = useCallback(
    (membership: MembershipPlan) => {
      // TODO (jonathan): Temporary disable switching between paid plans until upgrade/downgrade is implemented
      const notAvailable =
        currentMembershipPlan &&
        currentMembershipPlan.price !== 0 &&
        membership.price !== 0;

      const cardLabel = isCurrentMembershipPlan(membership.id)
        ? "Current Plan"
        : currentMembershipPlan
        ? notAvailable
          ? "Not Available"
          : currentMembershipPlan.price < membership.price
          ? "Upgrade"
          : "Downgrade"
        : "Select";
      return (
        <RectangleButton
          buttonLabel={cardLabel}
          onPress={() => chooseOnPress(membership)}
          altTextColor={
            isCurrentMembershipPlan(membership.id) || notAvailable
              ? Colors.GRAY1
              : selectedMembershipPlan?.id === membership.id
              ? theme.ButtonText.color
              : theme.PrimaryText.color
          }
          containerStyles={{
            padding: 14,
            borderRadius: 12,
            border: `1px solid ${
              isCurrentMembershipPlan(membership.id) || notAvailable
                ? theme.DividerColor.borderColor
                : selectedMembershipPlan?.id === membership.id
                ? Colors.BLACK
                : theme.DividerColor.borderColor
            }`,
            backgroundColor:
              isCurrentMembershipPlan(membership.id) || notAvailable
                ? theme.NewPrimaryBG.backgroundColor
                : selectedMembershipPlan?.id === membership.id
                ? theme.ButtonBG.backgroundColor
                : theme.NewPrimaryBG.backgroundColor,
          }}
          disabled={isCurrentMembershipPlan(membership.id) || notAvailable}
        />
      );
    },
    [
      chooseOnPress,
      currentMembershipPlan,
      isCurrentMembershipPlan,
      selectedMembershipPlan?.id,
      theme.ButtonBG.backgroundColor,
      theme.ButtonText.color,
      theme.DividerColor.borderColor,
      theme.NewPrimaryBG.backgroundColor,
      theme.PrimaryText.color,
    ]
  );

  return (
    <div
      className="ColumnNormal"
      style={{
        marginInline: "auto",
        width: isDesktop ? 544 : "100%",
        gap: 20,
      }}
    >
      <div
        className="ColumnCentering"
        style={{ gap: 20, paddingInline: isDesktop ? 0 : 14 }}
      >
        <div className="ColumnCentering" style={{ gap: isDesktop ? 30 : 14 }}>
          <ProfilePic size={isDesktop ? 100 : 75} user={creatorData} />
          <div className="ColumnCentering" style={{ gap: 7 }}>
            <span
              className="sectionTitle"
              style={{ textAlign: "center", ...theme.PrimaryText }}
            >
              {followerData
                ? "Change plan"
                : visibleMembershipPlans.length > 1
                ? "Choose a plan"
                : "Membership Plan"}
            </span>
            <span className="bodySubtext" style={{ fontWeight: 500 }}>
              @{creatorData.username}
            </span>
          </div>
        </div>
        {creatorData.bio ? (
          <div
            className="ColumnCentering"
            style={{ gap: 7, textAlign: "center", width: "90%" }}
          >
            <span className="bodySubtext" style={{ whiteSpace: "pre-wrap" }}>
              {creatorData.bio}
            </span>
          </div>
        ) : null}
      </div>
      {isDesktopSize && visibleMembershipPlans.length < 5 ? (
        <div
          className="AlignedRow"
          style={{ alignSelf: "center", alignItems: "stretch", gap: 24 }}
        >
          {visibleMembershipPlans.map((membership) => {
            const isSelected = selectedMembershipPlan?.id === membership.id;
            return (
              <StandardBorderedContainer
                key={membership.id}
                containerStyles={{
                  ...styles.cardContainer,
                  border: `1.5px solid ${
                    isSelected
                      ? theme.FocusedTextFieldBorder.borderColor
                      : theme.DividerColor.borderColor
                  }`,
                }}
                onPress={() => setSelectedMembershipPlan(membership)}
              >
                <div
                  className="ColumnNormalSpaced"
                  style={{ height: "100%", cursor: "pointer", gap: 14 }}
                >
                  <MembershipPlanCardDesktop
                    membership={membership}
                    themeType={themeType}
                  />
                  <div style={{ paddingInline: 14 }}>
                    {renderCardButton(membership)}
                  </div>
                </div>
              </StandardBorderedContainer>
            );
          })}
        </div>
      ) : (
        <div className="ColumnNormal" style={{ gap: 20, paddingInline: 14 }}>
          {visibleMembershipPlans.map((membership) => (
            <StandardBorderedContainer
              key={membership.id}
              containerStyles={{
                width: "100%",
                paddingBlock: 14,
                borderRadius: 14,
                border: `${
                  membership.id === selectedMembershipPlan?.id
                    ? `2px solid ${theme.FocusedTextFieldBorder.borderColor}`
                    : `1.5px solid ${theme.DividerColor.borderColor}`
                }`,
              }}
              onPress={() => setSelectedMembershipPlan(membership)}
            >
              <div className="ColumnNormal">
                <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
                  <div className="ColumnNormal" style={{ gap: 4 }}>
                    <span className="bodyMedium" style={theme.PrimaryText}>
                      {membership.name}
                    </span>
                    <span style={theme.SubText}>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>
                        ${membership.price / 100}
                      </span>{" "}
                      {membership.id !== "free" ? (
                        <span className="smallBodySubtext">
                          / {membership.billingFrequency}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  {membership.id === selectedMembershipPlan?.id ? (
                    <Icon
                      icon={
                        membership.id !== "free"
                          ? "ion:ribbon"
                          : "ion:checkmark-circle"
                      }
                      height={25}
                      style={{ color: theme.PrimaryText.color }}
                    />
                  ) : null}
                </div>
                {membership.id === selectedMembershipPlan?.id ? (
                  <>
                    <HorizontalDivider altMargin={14} eventTheme={themeType} />
                    {membershipDescription ? (
                      <div
                        className="ColumnNormal"
                        style={{ gap: 7, paddingInline: 14 }}
                      >
                        <span
                          style={{
                            ...theme.LabelText,
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          Benefits
                        </span>
                        <ReadOnlyRichTextEditor
                          body={membership.body}
                          theme={theme}
                          className={"post-content-editor"}
                        />
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            </StandardBorderedContainer>
          ))}
          {selectedMembershipPlan
            ? renderCardButton(selectedMembershipPlan)
            : null}
        </div>
      )}
      {followerData ? (
        <div
          className="AlignedRowSelect"
          style={{ justifyContent: "center", gap: 4 }}
          onClick={navigateProfilePage}
        >
          <span className="smallBodySubtext" style={{ fontWeight: 500 }}>
            Maybe later
          </span>
          <Icon
            icon="ion:chevron-right"
            height={11}
            style={{ color: Colors.GRAY1 }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default memo(SubscribeFlowMembership);
